import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2, ShieldAlert, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const sevTone = (s) => ({
    high: "bg-destructive/10 text-destructive border-destructive/30",
    medium: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    low: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
}[s]);

const kindLabel = (k) => ({
    duplicate_email: "Duplicate email",
    duplicate_resume_text: "Duplicate resume text",
    possible_identity_reuse: "Possible identity reuse",
    inactive_employer: "Inactive employer",
}[k] || k);

export default function FraudCenter() {
    const [data, setData] = useState({ count: 0, signals: [] });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/admin/fraud/signals");
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const buckets = {
        high: data.signals.filter((s) => s.severity === "high").length,
        medium: data.signals.filter((s) => s.severity === "medium").length,
        low: data.signals.filter((s) => s.severity === "low").length,
    };

    return (
        <div data-testid="admin-fraud-center">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Fraud Center
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Heuristic-based platform integrity signals.</p>
                </div>
                <button onClick={load} disabled={loading} className="btn-outline text-sm inline-flex items-center gap-1 disabled:opacity-60" data-testid="fraud-refresh">
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">High severity</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-destructive">{buckets.high}</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Medium</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-amber-600">{buckets.medium}</div>
                </div>
                <div className="card-soft p-4">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Low</div>
                    <div className="mt-1 font-heading font-bold text-2xl text-muted-foreground">{buckets.low}</div>
                </div>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : data.signals.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <ShieldAlert size={22} className="mx-auto text-emerald-500" />
                    <div className="mt-2 font-heading font-semibold">All clear.</div>
                    <div className="mt-1 text-xs text-muted-foreground">No fraud signals detected on the platform right now.</div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                <th className="p-3 font-semibold">Severity</th>
                                <th className="p-3 font-semibold">Kind</th>
                                <th className="p-3 font-semibold">Subject</th>
                                <th className="p-3 font-semibold">Detail</th>
                                <th className="p-3 font-semibold">References</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.signals.map((s) => (
                                <tr key={s.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30" data-testid={`fraud-row-${s.id}`}>
                                    <td className="p-3">
                                        <Badge className={`text-[10px] border ${sevTone(s.severity)}`}>{s.severity}</Badge>
                                    </td>
                                    <td className="p-3 text-xs text-foreground">{kindLabel(s.kind)}</td>
                                    <td className="p-3 text-xs text-foreground max-w-[260px] truncate">{s.subject}</td>
                                    <td className="p-3 text-xs text-muted-foreground max-w-[260px] truncate">{s.detail}</td>
                                    <td className="p-3 text-[10px] font-mono text-muted-foreground">
                                        {s.ref_ids?.slice(0, 3).map((id) => id.slice(0, 8)).join(", ")}
                                        {s.ref_ids?.length > 3 && ` +${s.ref_ids.length - 3}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
